import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CONFIRM_FIELD_BY_TARGET,
  DeletionMode,
  DeletionTargetDescriptor,
  DeletionTargetType,
} from "@/types/data-deletion";

interface DeletionConfirmBodyProps {
  descriptor: DeletionTargetDescriptor;
  isPending: boolean;
  serverError: string | null;
  onConfirm: (confirmation: string) => void;
  onCancel: () => void;
}

export const DeletionConfirmBody: React.FC<DeletionConfirmBodyProps> = ({
  descriptor,
  isPending,
  serverError,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const field = CONFIRM_FIELD_BY_TARGET[descriptor.type];
  const isAnonymize = descriptor.mode === DeletionMode.ANONYMIZE;
  const instructionKey =
    descriptor.type === DeletionTargetType.GUEST ? "guest" : field;

  // Reset the field whenever a different target is being confirmed.
  useEffect(() => {
    setValue("");
  }, [descriptor.id]);

  const trimmed = value.trim();
  const matches = trimmed === descriptor.expectedConfirmation;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!matches || isPending) {
      return;
    }
    onConfirm(trimmed);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <DialogHeader>
        <div className="flex items-center gap-2 text-custom-destructive-dark">
          <AlertTriangle className="size-5" />
          <DialogTitle className="text-custom-destructive-dark">
            {isAnonymize
              ? t("dataDeletion.confirm.titleAnonymize", {
                  label: descriptor.label,
                })
              : t("dataDeletion.confirm.title", { label: descriptor.label })}
          </DialogTitle>
        </div>
      </DialogHeader>

      <div className="rounded-lg border border-custom-destructive-dark/30 bg-custom-destructive-dark/5 p-4">
        <p className="text-sm text-custom-destructive-dark">
          {isAnonymize
            ? t("dataDeletion.confirm.warningAnonymize", {
                label: descriptor.label,
              })
            : t("dataDeletion.confirm.warning", { label: descriptor.label })}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="deletion-confirmation"
          className="text-sm font-medium text-foreground"
        >
          {t(`dataDeletion.confirm.instruction.${instructionKey}`)}
        </label>
        <p className="break-all font-mono text-sm text-muted-foreground">
          {descriptor.expectedConfirmation}
        </p>
        <Input
          id="deletion-confirmation"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={t("dataDeletion.confirm.inputLabel")}
          autoComplete="off"
          autoFocus
        />
      </div>

      {serverError ? (
        <div className="rounded-md border border-custom-destructive-dark/40 bg-custom-destructive-dark/5 p-3 text-sm text-custom-destructive-dark">
          {serverError}
        </div>
      ) : null}

      <DialogFooter>
        <Button
          type="button"
          variant="new_outline"
          onClick={onCancel}
          disabled={isPending}
        >
          {t("dataDeletion.confirm.cancel")}
        </Button>
        <Button
          type="submit"
          variant="new_destructive"
          disabled={isPending || !matches}
        >
          {(() => {
            if (isPending) {
              return t("dataDeletion.confirm.submitting");
            }
            return isAnonymize
              ? t("dataDeletion.confirm.submitAnonymize")
              : t("dataDeletion.confirm.submit");
          })()}
        </Button>
      </DialogFooter>
    </form>
  );
};
